/**
 * The card page's Worksheet tab (TMMPDM-0089, tmm-api 0154 section H): a hypothetical subscriber,
 * the customer's bill by line, how it's worked out, and who earns what by line. Every figure is the
 * server's worksheet (`POST …/versions/{versionId}/worksheet`) as sent: the console adds, subtracts
 * and converts nothing — it echoes the inputs and formats the answer. Costs arrive negative and render
 * in parentheses, in the error colour. The inputs live in the URL (a shareable what-if); a change
 * re-requests after 300 ms, and the last answer stays on screen, marked as updating, while it loads.
 * TMMPDM-0090 (tmm-api 0155 item 22a): the customer's bill is the server's `bill`, line by line.
 * TMMPDM-0091: each profit figure carries the server's percent of what the customer pays
 * (`resellerProfitBp`, `ourProfitBp`), formatted only; `null` shows nothing.
 */
import { useEffect, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { adminApi } from '@/api/tmm/admin';
import type {
  AdminWorksheet,
  CustomerRateCardVersionView,
  WorksheetBill,
  WorksheetBillLine,
  WorksheetBillLineKind,
  WorksheetLine,
  WorksheetLineKind,
  WorksheetRequest,
} from '@/api/tmm/types';
import { ErrorState, SectionCard } from '@/components/page';
import { Amount } from '@/components/ui';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { NOT_SENT, formatMoney, formatNumber, formatPercentBp } from '@/format';
import { invalidMoney, parseMajor } from './rateCardInputs';

const WORKSHEET_DEBOUNCE_MS = 300;
/** tmm-api's worksheet bounds for guests and connections. */
const COUNT_MAX = 50;
const MODES = ['monthly', 'annual'] as const;

type WorksheetUrl = {
  mode: (typeof MODES)[number];
  guests: string;
  connections: string;
  ai: string;
};

const asText = (raw: string) => raw;
const SCHEMA: UrlSchema<WorksheetUrl> = {
  mode: { default: 'monthly', parse: oneOf(MODES) },
  guests: { default: '2', parse: asText },
  connections: { default: '2', parse: asText },
  ai: { default: '0', parse: asText },
};

function invalidCount(value: string): boolean {
  if (!/^\d+$/.test(value.trim())) return true;
  return Number(value) > COUNT_MAX;
}

type Answer =
  | { key: string; worksheet: AdminWorksheet; error?: undefined }
  | { key: string; error: unknown; worksheet?: undefined };

export function WorksheetTab({
  cardId,
  currency,
  version,
}: {
  cardId: number;
  currency: string;
  version: CustomerRateCardVersionView;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [url, setUrl] = useUrlState(SCHEMA);
  const [inputs, setInputs] = useState({
    guests: url.guests,
    connections: url.connections,
    ai: url.ai,
  });
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [retry, setRetry] = useState(0);

  const noAnnual = version.annualBaseMinor === null;
  const mode = noAnnual ? 'monthly' : url.mode;
  const errors = {
    guests: invalidCount(inputs.guests),
    connections: invalidCount(inputs.connections),
    ai: invalidMoney(inputs.ai),
  };
  const valid = !errors.guests && !errors.connections && !errors.ai;
  const request: WorksheetRequest | null = valid
    ? {
        mode: mode === 'annual' ? 'ANNUAL' : 'MONTHLY',
        guests: Number(inputs.guests),
        connections: Number(inputs.connections),
        aiUsedMinor: parseMajor(inputs.ai, currency),
      }
    : null;
  const requestKey = request ? `${version.id}:${retry}:${JSON.stringify(request)}` : null;

  useEffect(() => {
    if (request === null || requestKey === null) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      adminApi
        .customerRateCardWorksheet(cardId, version.id, request, { signal: controller.signal })
        .then((worksheet) => {
          if (!controller.signal.aborted) setAnswer({ key: requestKey, worksheet });
        })
        .catch((error: unknown) => {
          if (!controller.signal.aborted) setAnswer({ key: requestKey, error });
        });
    }, WORKSHEET_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
    // `request` is fully described by `requestKey`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, version.id, requestKey]);

  const change = (key: keyof typeof inputs, value: string) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
    setUrl({ [key]: value }, { history: 'replace' });
  };

  const shown = answer?.worksheet;
  const updating = requestKey !== null && answer?.key !== requestKey;
  const perYear = mode === 'annual';

  return (
    <Stack spacing={3} aria-busy={updating}>
      <Box component="section" aria-labelledby="worksheet-subscriber-title">
        <Typography id="worksheet-subscriber-title" variant="h2" component="h2" gutterBottom>
          {t('rateCard.worksheet.subscriber', 'A hypothetical subscriber')}
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Stack spacing={2}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={mode}
                aria-label={t('rateCard.worksheet.mode', 'Billing mode')}
                onChange={(_event, next: string | null) => {
                  if (next === 'monthly' || next === 'annual') setUrl({ mode: next });
                }}
              >
                <ToggleButton value="monthly">
                  {t('rateCard.worksheet.monthly', 'Monthly')}
                </ToggleButton>
                <ToggleButton value="annual" disabled={noAnnual}>
                  {t('rateCard.worksheet.annual', 'Annual')}
                </ToggleButton>
              </ToggleButtonGroup>
              {noAnnual ? (
                <Typography variant="body2" color="text.secondary">
                  {t('rateCard.worksheet.noAnnual', 'This version has no annual price.')}
                </Typography>
              ) : null}
              <TextField
                id="worksheet-guests"
                label={t('rateCard.worksheet.guests', 'Guests')}
                value={inputs.guests}
                onChange={(event) => change('guests', event.target.value)}
                error={errors.guests}
                helperText={
                  errors.guests
                    ? t('rateCard.worksheet.countRange', 'A whole number from 0 to 50.')
                    : undefined
                }
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              />
              <TextField
                id="worksheet-connections"
                label={t('rateCard.worksheet.connections', 'Bank accounts connected')}
                value={inputs.connections}
                onChange={(event) => change('connections', event.target.value)}
                error={errors.connections}
                helperText={
                  errors.connections
                    ? t('rateCard.worksheet.countRange', 'A whole number from 0 to 50.')
                    : undefined
                }
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              />
              <TextField
                id="worksheet-ai"
                label={t('rateCard.worksheet.aiUsed', 'AI used per month (customer price)')}
                value={inputs.ai}
                onChange={(event) => change('ai', event.target.value)}
                error={errors.ai}
                helperText={
                  errors.ai
                    ? t('rateCards.form.nonNegative', 'Must be zero or greater.')
                    : formatMoney(parseMajor(inputs.ai, currency), currency, locale)
                }
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            {updating ? (
              <LinearProgress
                aria-label={t('rateCard.worksheet.updating', 'Updating the worksheet')}
                sx={{ mb: 1 }}
              />
            ) : null}
            {answer?.error !== undefined && !updating ? (
              <ErrorState
                error={answer.error}
                message={t('rateCard.worksheet.loadError', "Couldn't work out this worksheet.")}
                onRetry={() => setRetry((n) => n + 1)}
              />
            ) : null}
            {!valid ? (
              <Alert severity="info">
                {t('rateCard.worksheet.fixInputs', 'Fix the inputs to see the worksheet.')}
              </Alert>
            ) : null}
            {shown ? (
              <>
                <BillTable bill={shown.bill} currency={currency} />
                <StepsBox worksheet={shown} currency={currency} />
              </>
            ) : null}
          </Grid>
        </Grid>
      </Box>
      {shown ? <EarningsSection worksheet={shown} currency={currency} perYear={perYear} /> : null}
    </Stack>
  );
}

function useLineLabels(): (kind: WorksheetLineKind) => string {
  const { t } = useCopy();
  return (kind) => {
    switch (kind) {
      case 'BASE':
        return t('rateCard.worksheet.line.BASE', 'Base');
      case 'GUESTS':
        return t('rateCard.worksheet.line.GUESTS', 'Guests');
      case 'CONNECTIONS':
        return t('rateCard.worksheet.line.CONNECTIONS', 'Bank connections');
      case 'SUBSCRIPTION_PROCESSING':
        return t(
          'rateCard.worksheet.line.SUBSCRIPTION_PROCESSING',
          'Card processing on the subscription',
        );
      case 'AI_PAID':
        return t('rateCard.worksheet.line.AI_PAID', 'AI the customer pays for');
      case 'AI_CREDIT':
        return t('rateCard.worksheet.line.AI_CREDIT', 'AI covered by the credit');
      case 'RECHARGE_PROCESSING':
        return t('rateCard.worksheet.line.RECHARGE_PROCESSING', 'Card processing on recharges');
      default:
        return t('rateCard.worksheet.line.TOTAL', 'Total');
    }
  };
}

/** The grey "how it's worked out" box: the server's steps, as sent. */
function StepsBox({ worksheet, currency }: { worksheet: AdminWorksheet; currency: string }) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const steps = worksheet.steps;
  const money = (minor: number) => formatMoney(minor, currency, locale);
  const chargeKind = (kind: string) =>
    kind === 'BASE'
      ? t('rateCard.worksheet.charge.BASE', 'Base plan')
      : kind === 'ADD_ONS'
        ? t('rateCard.worksheet.charge.ADD_ONS', 'Guests and connections')
        : kind === 'RECHARGE'
          ? t('rateCard.worksheet.charge.RECHARGE', 'Wallet recharge')
          : kind;
  return (
    <Box sx={{ mt: 2 }}>
      <SectionCard
        headingLevel="h3"
        id="worksheet-steps-title"
        title={t('rateCard.worksheet.steps', "How it's worked out")}
      >
        <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
          <Typography component="li" variant="body2">
            {t('rateCard.worksheet.chargesPerYear', 'Subscription charges a year: {{n, number}}.', {
              n: steps.subscriptionChargesPerYear,
            })}
          </Typography>
          {steps.charges.map((charge, index) => (
            <Typography component="li" variant="body2" key={`${charge.kind}-${index}`}>
              {t(
                'rateCard.worksheet.chargeLine',
                '{{kind}}: {{gross}} charged {{perYear, number}} times a year. Card fee {{fee}}: the reseller pays {{reseller}}, we pay {{platform}}.',
                {
                  kind: chargeKind(charge.kind),
                  gross: money(charge.grossMinor),
                  perYear: charge.perYear,
                  fee: money(charge.feeMinor),
                  reseller: money(charge.resellerShareMinor),
                  platform: money(charge.platformShareMinor),
                },
              )}
            </Typography>
          ))}
          <Typography component="li" variant="body2">
            {steps.feePercentBp === null || steps.feeFixedMinor === null
              ? t(
                  'rateCard.worksheet.noRate',
                  'No card processing rate is set for {{currency}}, so no fee is counted.',
                  { currency },
                )
              : t(
                  'rateCard.worksheet.rate',
                  'Card processing: {{percent}} + {{fixed}} per charge.',
                  {
                    percent: formatPercentBp(steps.feePercentBp, locale),
                    fixed: money(steps.feeFixedMinor),
                  },
                )}
          </Typography>
          <Typography component="li" variant="body2">
            {t(
              'rateCard.worksheet.recharges',
              'Automatic wallet recharges a month: {{n, number}}.',
              {
                n: steps.recharges,
              },
            )}
          </Typography>
          <Typography component="li" variant="body2">
            {t('rateCard.worksheet.creditCost', 'The monthly AI credit costs us {{amount}}.', {
              amount: money(steps.creditCostMinor),
            })}
          </Typography>
        </Stack>
      </SectionCard>
    </Box>
  );
}

/** Who earns what, by line and in total, as the server sent it. */
function EarningsSection({
  worksheet,
  currency,
  perYear,
}: {
  worksheet: AdminWorksheet;
  currency: string;
  perYear: boolean;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const lineLabel = useLineLabels();
  const totals = worksheet.totals;
  const figures: { key: string; label: string; minor: number; percentBp?: number | null }[] = [
    {
      key: 'customer',
      label: t('rateCard.worksheet.customerPays', 'Customer pays'),
      minor: totals.customerPaysMinor,
    },
    {
      key: 'reseller',
      label: t('rateCard.worksheet.resellerProfit', 'Reseller profit'),
      minor: totals.resellerProfitMinor,
      percentBp: worksheet.resellerProfitBp,
    },
    {
      key: 'ours',
      label: t('rateCard.worksheet.ourProfit', 'Our profit'),
      minor: totals.ourProfitMinor,
      percentBp: worksheet.ourProfitBp,
    },
  ];
  const cells = (line: WorksheetLine, emphasis: boolean) =>
    [
      line.customerPaysMinor,
      line.resellerCostsMinor,
      line.resellerProfitMinor,
      line.ourCostMinor,
      line.ourProfitMinor,
    ].map((minor, index) => (
      <TableCell align="right" key={index}>
        <Amount minor={minor} currency={currency} accounting emphasis={emphasis} />
      </TableCell>
    ));
  return (
    <Box component="section" aria-labelledby="worksheet-earnings-title">
      <Typography id="worksheet-earnings-title" variant="h2" component="h2" gutterBottom>
        {perYear
          ? t(
              'rateCard.worksheet.earningsAnnual',
              'Who earns what, per month (the annual plan ÷ 12)',
            )
          : t('rateCard.worksheet.earnings', 'Who earns what, per month')}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        {figures.map((figure) => (
          <Paper key={figure.key} variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {figure.label}
            </Typography>
            <Typography variant="h3" component="p">
              <Amount minor={figure.minor} currency={currency} accounting />
            </Typography>
            {figure.percentBp !== undefined && figure.percentBp !== null ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={(theme) => ({ ...theme.typography.numeric })}
              >
                {t('rateCard.worksheet.profitPercent', '{{percent}} of what the customer pays', {
                  percent: formatPercentBp(figure.percentBp, i18n.language),
                })}
              </Typography>
            ) : null}
          </Paper>
        ))}
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table
          size="small"
          aria-label={t('rateCard.worksheet.earningsAria', 'Who earns what, by line')}
        >
          <TableHead>
            <TableRow>
              <TableCell>{t('rateCard.worksheet.col.line', 'Line')}</TableCell>
              <TableCell align="right">
                {t('rateCard.worksheet.customerPays', 'Customer pays')}
              </TableCell>
              <TableCell align="right">
                {t('rateCard.worksheet.resellerCosts', 'Reseller costs')}
              </TableCell>
              <TableCell align="right">
                {t('rateCard.worksheet.resellerProfit', 'Reseller profit')}
              </TableCell>
              <TableCell align="right">{t('rateCard.worksheet.ourCost', 'Our cost')}</TableCell>
              <TableCell align="right">{t('rateCard.worksheet.ourProfit', 'Our profit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {worksheet.lines.map((line) => (
              <TableRow key={line.line}>
                <TableCell component="th" scope="row">
                  {lineLabel(line.line)}
                </TableCell>
                {cells(line, false)}
              </TableRow>
            ))}
            <TableRow>
              <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                {lineLabel('TOTAL')}
              </TableCell>
              {cells(totals, true)}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t(
          'rateCard.worksheet.note',
          "Reseller profit = customer pays + reseller costs. Our profit = the wholesale the reseller pays us + our cost. Affiliate commission comes out of the reseller's profit.",
        )}
      </Typography>
    </Box>
  );
}

/**
 * The customer's bill (TMMAPI-0155 item 22a), rendered as sent: one row per line in the server's
 * order — the subscription lines, the subtotal for the server's `period`, then the AI lines and
 * what the wallet pays. Quantity and unit price show when sent (a band's flat as its unit price);
 * an absent one shows the absent value. A negative amount (the AI credit) is red and in
 * parentheses. Nothing is added, split or multiplied here.
 */
function BillTable({ bill, currency }: { bill: WorksheetBill; currency: string }) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const yearly = bill.period === 'YEAR';
  const label = (kind: WorksheetBillLineKind): string => {
    switch (kind) {
      case 'BASE':
        return yearly
          ? t('rateCard.worksheet.bill.BASE_YEAR', 'Base subscription, 1 year')
          : t('rateCard.worksheet.bill.BASE', 'Base subscription');
      case 'GUESTS':
        return yearly
          ? t('rateCard.worksheet.bill.GUESTS_YEAR', 'Guests, 12 months')
          : t('rateCard.worksheet.bill.GUESTS', 'Guests');
      case 'CONNECTIONS':
        return yearly
          ? t('rateCard.worksheet.bill.CONNECTIONS_YEAR', 'Bank connections, 12 months')
          : t('rateCard.worksheet.bill.CONNECTIONS', 'Bank connections');
      case 'AI_USAGE':
        return t('rateCard.worksheet.bill.AI_USAGE', 'AI usage this month (wallet)');
      default:
        return t('rateCard.worksheet.bill.AI_CREDIT', 'Monthly AI credit');
    }
  };
  const unitPrice = (line: WorksheetBillLine): ReactNode => {
    const flat = line.flatMinor;
    const unit = line.unitPriceMinor;
    if (flat !== undefined && unit !== undefined) {
      return t('rateCard.worksheet.bill.flatAndUnit', '{{flat}} + {{unit}} each', {
        flat: formatMoney(flat, currency, locale),
        unit: formatMoney(unit, currency, locale),
      });
    }
    if (flat !== undefined) return <Amount minor={flat} currency={currency} />;
    if (unit !== undefined) return <Amount minor={unit} currency={currency} />;
    return NOT_SENT;
  };
  const row = (key: string, name: string, line: WorksheetBillLine | null, amountMinor: number) => (
    <TableRow key={key}>
      <TableCell component="th" scope="row" sx={line ? undefined : { fontWeight: 600 }}>
        {name}
      </TableCell>
      <TableCell align="right">
        {line === null
          ? ''
          : line.quantity === undefined
            ? NOT_SENT
            : formatNumber(line.quantity, locale)}
      </TableCell>
      <TableCell align="right">{line === null ? '' : unitPrice(line)}</TableCell>
      <TableCell align="right">
        <Amount minor={amountMinor} currency={currency} accounting emphasis={line === null} />
      </TableCell>
    </TableRow>
  );
  const subscription = bill.lines.filter((line) => !AI_BILL_KINDS.includes(line.kind));
  const ai = bill.lines.filter((line) => AI_BILL_KINDS.includes(line.kind));
  return (
    <>
      <Typography variant="h3" component="h3" gutterBottom>
        {yearly
          ? t('rateCard.worksheet.billYearly', "The customer's bill (billed yearly)")
          : t('rateCard.worksheet.billMonthly', "The customer's bill (billed monthly)")}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('rateCard.worksheet.billAria', "The customer's bill")}>
          <TableHead>
            <TableRow>
              <TableCell>{t('rateCard.worksheet.col.line', 'Line')}</TableCell>
              <TableCell align="right">{t('rateCard.worksheet.col.quantity', 'Qty')}</TableCell>
              <TableCell align="right">
                {t('rateCard.worksheet.col.unitPrice', 'Unit price')}
              </TableCell>
              <TableCell align="right">{t('rateCard.worksheet.col.amount', 'Amount')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subscription.map((line, index) =>
              row(`s${index}`, label(line.kind), line, line.amountMinor),
            )}
            {row(
              'subtotal',
              yearly
                ? t('rateCard.worksheet.bill.subtotalYear', 'Subscription, per year')
                : t('rateCard.worksheet.bill.subtotalMonth', 'Subscription, per month'),
              null,
              bill.subscriptionSubtotalMinor,
            )}
            {ai.map((line, index) => row(`a${index}`, label(line.kind), line, line.amountMinor))}
            {row(
              'aiNet',
              t('rateCard.worksheet.bill.aiNet', 'AI paid from the wallet, per month'),
              null,
              bill.aiNetMinor,
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

/** The AI lines follow the subscription subtotal; each group keeps the server's order. */
const AI_BILL_KINDS: readonly WorksheetBillLineKind[] = ['AI_USAGE', 'AI_CREDIT'];
